import { useAuth } from "@/app/hooks/useAuth";
import { ReportTransaction } from "@/app/services/reportService/reportTransactions";
import { cn } from "@/app/utils/cn";
import { comissionPercentage } from "@/app/utils/comissionPercentage";
import { formatMoneyValue } from "@/app/utils/formatMoneyValue";
import { formatName } from "@/app/utils/formatName";
import { getImageByType } from "@/app/utils/getImageByType";
import Status from "@/view/components/Status";
import { createColumnHelper } from "@tanstack/react-table";
import { format } from "date-fns";
import { useMemo } from "react";

const columnHelper = createColumnHelper<ReportTransaction>();

export const TableReportTransactionsColumnsDefinition = () => {
  const { isSeller } = useAuth();
  const columns = useMemo(
    () => [
      columnHelper.accessor("contract", {
        id: "contract",
        header: () => (
          <div className="flex flex-col items-center">
            <div>Contrato</div>
            <div className="block text-black04 xl:hidden">Data</div>
            <div className="block text-center text-black04 xl:hidden">
              Data de atualização
            </div>
            <div className="block text-black04 md:hidden">Tabela</div>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex flex-col items-center gap-1">
            <div>{row.original.contract}</div>
            <div className="block text-black04 xl:hidden">
              {format(row.original.createdAt, "dd/MM/yyyy, HH:mm")}
            </div>
            <div className="block text-black04 xl:hidden">
              {format(row.original.updatedAt, "dd/MM/yyyy, HH:mm")}
            </div>
            <div className="block text-black04 md:hidden">
              {row.original.table}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("type", {
        id: "type",
        header: () => (
          <div className="hidden text-center xs:block">Produto</div>
        ),
        cell: ({ row }) => {
          const type = row.original.type;
          const imageSrc = getImageByType(type);

          return (
            <div className="hidden w-full flex-col items-center justify-center text-center xs:flex">
              <img
                src={imageSrc}
                className="mb-1 h-8 w-8 rounded-lg object-contain"
                alt={type}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor("seller", {
        id: "vendedor",
        header: () => (
          <div className="hidden pl-4 text-left lg:block">
            {!isSeller ? "Vendedor" : "Cliente"}
          </div>
        ),
        cell: ({ row }) => (
          <div className="hidden pl-4 text-left lg:block">
            <p>
              {formatName(
                !isSeller ? row.original.seller : row.original.client,
              )}
            </p>
          </div>
        ),
      }),
      columnHelper.accessor("value", {
        id: "value",
        header: () => (
          <div className="flex flex-col items-start pl-4">
            <div className="hidden lg:block">Valor</div>
            <div className="block lg:hidden">Valor/Comissão</div>
            <div className="block text-black04 lg:hidden">Vendedor</div>
            <div className="block text-black04 md:hidden">Status</div>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex flex-col items-start gap-1 pl-4">
            <div className="hidden lg:block">
              {formatMoneyValue(row.original.value)}
            </div>
            <div className="block lg:hidden">
              {formatMoneyValue(row.original.value)}
              {" | "}
              {formatMoneyValue(row.original.commission)}{" "}
              {comissionPercentage(row.original.value, row.original.commission)}
              {!isSeller &&
                ` | ${formatMoneyValue(row.original.leaderCommission || 0)} ${comissionPercentage(
                  row.original.value,
                  row.original.leaderCommission || 0,
                )}`}
            </div>
            <div className="block text-black04 lg:hidden">
              {row.original.seller}
            </div>
            <div className="block text-black04 md:hidden">
              <Status status={row.getValue("status")} />
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("commission", {
        id: "commission",
        header: () => (
          <div className="hidden flex-col items-center  px-4 lg:flex">
            <div>Comissão</div>
          </div>
        ),
        cell: ({ row }) => (
          <div className="hidden flex-col items-start gap-1 pl-4 lg:flex">
            <div className="hidden text-center lg:block">
              {formatMoneyValue(row.original.commission)}{" "}
              {comissionPercentage(row.original.value, row.original.commission)}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("client", {
        id: "client",
        header: () => (
          <div
            className={cn("hidden text-left md:block", isSeller && "md:hidden")}
          >
            Cliente
          </div>
        ),
        cell: ({ row }) => (
          <div
            className={cn("hidden text-left md:block", isSeller && "md:hidden")}
          >
            {formatName(row?.original?.client ?? "")}
          </div>
        ),
      }),

      columnHelper.accessor("table", {
        id: "tabela",
        header: () => <div className="hidden text-center md:block">Tabela</div>,
        cell: ({ row }) => (
          <div className="hidden text-center md:block">
            {row.original.table}
          </div>
        ),
      }),
      columnHelper.accessor("createdAt", {
        id: "createdAt",
        header: () => (
          <div className="hidden pr-4 text-center xl:block">
            Data de criação
          </div>
        ),
        cell: ({ row }) => (
          <div className="hidden pr-4 text-center xl:block">
            {format(row.original.createdAt, "dd/MM/yyyy, HH:mm")}
          </div>
        ),
      }),
      columnHelper.accessor("updatedAt", {
        id: "updatedAt",
        header: () => (
          <div className="hidden pr-4 text-center xl:block">
            Data de atualização
          </div>
        ),
        cell: ({ row }) => (
          <div className="hidden pr-4 text-center xl:block">
            {format(row.original.updatedAt, "dd/MM/yyyy, HH:mm")}
          </div>
        ),
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="hidden text-center md:block">Status</div>,
        cell: ({ row }) => (
          <div className="hidden md:block">
            <Status status={row.getValue("status")} />
          </div>
        ),
      }),
    ],
    [isSeller],
  );
  return {
    columns,
  };
};
